import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { v: "org_admin", label: "Org Admin" },
  { v: "org_member", label: "Org Member" },
  // { v: "org_viewer", label: "Org Viewer" },
];

export default function Team() {
  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "org_member" });
  const [busy, setBusy] = useState(false);

  // P2 #10: edit + delete state
  const [editTarget, setEditTarget] = useState(null);  // member doc being edited
  const [editForm, setEditForm] = useState({ full_name: "", email: "", role: "org_member", password: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/org/members").then((r) => setMembers(r.data.members || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/org/members", form);
      toast.success("Member invited");
      setForm({ email: "", full_name: "", password: "", role: "org_member" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to invite");
    } finally { setBusy(false); }
  };

  // ----- Edit -----
  const openEdit = (m) => {
    setEditTarget(m);
    setEditForm({
      full_name: m.full_name || "",
      email: m.email || "",
      role: m.role || "org_member",
      password: "",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditBusy(true);
    try {
      const payload = {
        full_name: editForm.full_name,
        email: editForm.email,
        role: editForm.role,
      };
      if (editForm.password && editForm.password.length >= 8) {
        payload.password = editForm.password;
      }
      await api.patch(`/org/members/${editTarget.id}`, payload);
      toast.success("Member updated");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setEditBusy(false); }
  };

  // ----- Delete -----
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/org/members/${deleteTarget.id}`);
      toast.success("Member removed");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      setDeleteTarget(null);
    }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="team-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="type-eyebrow text-[#64748B]">Organisation</div>
          <h1 className="type-h1 text-[#0F1F3D] mt-3">Team</h1>
        </div>
        {!isOrgMember && (
          <button onClick={() => setOpen((v) => !v)} data-testid="invite-member-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white type-body-sm font-medium hover:bg-[#172A4F]">
            <Plus className="h-4 w-4" /> Invite member
          </button>
        )}
      </div>

      {open && !isOrgMember && (
        <form onSubmit={submit} className="mt-8 bg-white border border-[#E2E8F0] rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <input required placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            data-testid="invite-name-input"
            className="px-3 py-2.5 rounded-md border border-[#E2E8F0] type-body" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="invite-email-input"
            className="px-3 py-2.5 rounded-md border border-[#E2E8F0] type-body" />
          <input required type="password" placeholder="Temp password (min 8)" minLength={8} value={form.password}
            data-testid="invite-password-input"
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="px-3 py-2.5 rounded-md border border-[#E2E8F0] font-mono-tab type-body-sm" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role-select"
            className="px-3 py-2.5 rounded-md border border-[#E2E8F0] bg-white type-body">
            {ROLE_OPTIONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
          <button type="submit" disabled={busy} data-testid="invite-submit-button"
            className="md:col-span-2 px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white type-body-sm font-medium hover:bg-[#172A4F] disabled:opacity-50">
            {busy ? "Inviting…" : "Send invite"}
          </button>
        </form>
      )}

      <div className="mt-8 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F8F9FA] type-eyebrow text-[#64748B]">
            <tr>
              <th className="text-left px-6 py-3">Name</th>
              <th className="text-left px-6 py-3">Email</th>
              <th className="text-left px-6 py-3">Role</th>
              {!isOrgMember && <th className="text-right px-6 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} data-testid={`team-row-${m.id}`} className="border-t border-[#E2E8F0]">
                <td className="px-6 py-4 type-h4 text-[#0F1F3D]">{m.full_name}</td>
                <td className="px-6 py-4 type-body-sm text-[#475569]">{m.email}</td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 rounded-md bg-[#F1F5F9] type-eyebrow text-[#475569]">
                    {m.role.replace("_", " ")}
                  </span>
                </td>
                {!isOrgMember && (
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(m)}
                        data-testid={`team-edit-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E2E8F0] text-[#0F1F3D] type-caption font-semibold hover:bg-[#F1F5F9]"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                      <button
                        onClick={() => setDeleteTarget(m)}
                        data-testid={`team-delete-${m.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#FECACA] text-[#B91C1C] type-caption font-semibold hover:bg-[#FEF2F2]"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {members.length === 0 && (
              <tr><td colSpan={isOrgMember ? 3 : 4} className="px-6 py-12 text-center type-body text-[#94A3B8]">No team members yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editTarget && (
        <div
          data-testid="team-edit-modal"
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => !editBusy && setEditTarget(null)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitEdit}
            className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 grid grid-cols-1 gap-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="type-h2 text-[#0F1F3D]">Edit member</h2>
              <button type="button" onClick={() => setEditTarget(null)} className="p-1 rounded-md hover:bg-[#F1F5F9]">
                <X className="h-4 w-4 text-[#64748B]" />
              </button>
            </div>

            <div>
              <label className="type-eyebrow text-[#64748B]">Full name</label>
              <input
                required value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                data-testid="team-edit-name-input"
                className="mt-2 w-full px-3 py-2.5 rounded-md border border-[#E2E8F0] type-body"
              />
            </div>

            <div>
              <label className="type-eyebrow text-[#64748B]">Email</label>
              <input
                required type="email" value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="team-edit-email-input"
                className="mt-2 w-full px-3 py-2.5 rounded-md border border-[#E2E8F0] type-body"
              />
            </div>

            <div>
              <label className="type-eyebrow text-[#64748B]">Role</label>
              <select
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                data-testid="team-edit-role-select"
                className="mt-2 w-full px-3 py-2.5 rounded-md border border-[#E2E8F0] bg-white type-body"
              >
                {ROLE_OPTIONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>

            <div>
              <label className="type-eyebrow text-[#64748B]">New password (optional)</label>
              <input
                type="password" minLength={8} placeholder="Leave blank to keep current"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                data-testid="team-edit-password-input"
                className="mt-2 w-full px-3 py-2.5 rounded-md border border-[#E2E8F0] font-mono-tab type-body-sm"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                disabled={editBusy}
                className="px-4 py-2.5 rounded-md border border-[#E2E8F0] text-[#475569] type-body-sm font-medium hover:bg-[#F1F5F9]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={editBusy}
                data-testid="team-edit-submit"
                className="px-4 py-2.5 rounded-md bg-[#0F1F3D] text-white type-body-sm font-medium hover:bg-[#172A4F] disabled:opacity-50"
              >
                {editBusy ? "Updating…" : "Update member"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="team-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `This will permanently remove ${deleteTarget.full_name} (${deleteTarget.email}) from your organisation. This action cannot be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="team-delete-confirm"
              onClick={confirmDelete}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
            >
              Remove member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
